import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      // Fetch organization + permissions in parallel (best-effort)
      try {
        const meRes = await axios.get(`${API_URL}/api/team/me`);
        setMembership(meRes.data);
      } catch (e) {
        // pre-migration users — fallback to full permissions
        setMembership({ role: 'owner', permissions: {}, can_see_all_data: true });
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      remember_me: !!rememberMe,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    // Remember the email between visits so the next login is one tap away (the browser's password
    // manager then handles Face ID / Touch ID natively via the standard autocomplete attributes).
    if (rememberMe) {
      localStorage.setItem('remembered_email', email);
    } else {
      localStorage.removeItem('remembered_email');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    try {
      const meRes = await axios.get(`${API_URL}/api/team/me`);
      setMembership(meRes.data);
    } catch (e) {
      setMembership({ role: 'owner', permissions: {}, can_see_all_data: true });
    }
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    try {
      const meRes = await axios.get(`${API_URL}/api/team/me`);
      setMembership(meRes.data);
    } catch (e) {
      setMembership({ role: 'owner', permissions: {}, can_see_all_data: true });
    }
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setMembership(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  // Helper: check if user has access to a given module.
  const hasPermission = (moduleName) => {
    if (!membership) return true; // pre-load: assume access
    const role = membership.role || 'member';
    if (role === 'owner' || role === 'admin') return true;
    const p = membership.permissions || {};
    return !!p[moduleName];
  };

  const isOwnerOrAdmin = () => {
    const role = membership?.role;
    return role === 'owner' || role === 'admin';
  };

  // Returns whether the organization has subscribed to a given optional module.
  // Starter-pack modules and unknown keys always return true.
  const isModuleEnabled = (moduleKey) => {
    if (!membership) return true;
    const available = membership.available_optional_modules || [];
    if (!available.includes(moduleKey)) return true; // not optional → always on
    const enabled = membership.enabled_optional_modules || [];
    return enabled.includes(moduleKey);
  };

  const enableModule = async (moduleKey) => {
    const r = await axios.post(`${API_URL}/api/team/enable-module`, {
      module: moduleKey,
      enabled: true,
    });
    setMembership((prev) =>
      prev ? { ...prev, enabled_optional_modules: r.data.enabled_optional_modules } : prev,
    );
    return r.data;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, membership,
      login, register, logout, updateUser,
      hasPermission, isOwnerOrAdmin,
      isModuleEnabled, enableModule,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
